import { belongsToOkrTeam } from './okrTeamScope';
import { getCalculationTypeLabel } from './okrCalculationLabel';
import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Tag } from 'primereact/tag';

const getId = (value) => value?._id || value?.id || value || '';

const formatNumber = (value) => {
    const number = Number(value || 0);
    return Number.isFinite(number) ? number.toLocaleString('es-PA', { maximumFractionDigits: 2 }) : '0';
};

const getUserName = (user) => {
    if (!user) return '-';
    if (typeof user === 'string') return user;
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || user.email || '-';
};

const getPointOwnerId = (keyResult) => {
    return getId(keyResult?.ownerId || keyResult?.pointOwnerUserId || keyResult?.updateResponsibleUserId);
};

const getPointOwnerName = (keyResult, users = []) => {
    const ownerId = getPointOwnerId(keyResult);
    return getUserName(users.find((item) => getId(item) === ownerId) || keyResult?.ownerId || keyResult?.pointOwnerUserId || keyResult?.updateResponsibleUserId);
};


const getStatusLabel = (status) => {
    const labels = {
        pending: 'Pendiente',
        draft: 'Borrador',
        submitted: 'Enviada',
        approved: 'Aprobada',
        rejected: 'Rechazada'
    };
    return labels[status] || 'Sin actualizar';
};

const getStatusSeverity = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'submitted') return 'info';
    if (status === 'rejected') return 'danger';
    if (status === 'draft') return 'warning';
    if (status === 'pending') return 'secondary';
    return 'secondary';
};

const getSubmittedDate = (entry) => entry?.submittedAt || entry?.updatedAt || entry?.createdAt || null;

const AUTOMATED_SOURCE_LABELS = {
    quickbooks: 'QuickBooks',
    parlevel: 'Parlevel',
    syncro_operations: 'Syncro',
    composite: 'Compuesta'
};

const formatDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('es-PA');
};

const OkrWeeklyUpdates = ({
    selectedCycle,
    currentWeek,
    selectedWeekNumber,
    onWeekChange,
    teams,
    objectives,
    keyResults,
    weeklyEntries,
    users,
    currentUser,
    canManage,
    loading,
    reviewingEntryId,
    onApproveEntry,
    onRejectEntry
}) => {
    const [teamFilter, setTeamFilter] = useState('');
    const [ownerFilter, setOwnerFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('submitted');
    const [rejectDialogVisible, setRejectDialogVisible] = useState(false);
    const [rejectEntry, setRejectEntry] = useState(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [rejectionError, setRejectionError] = useState('');

    const weeks = useMemo(() => (Array.isArray(selectedCycle?.weeks) ? selectedCycle.weeks : []), [selectedCycle]);
    const currentUserId = getId(currentUser);
    const managedTeamIds = useMemo(
        () => new Set(teams.filter((team) => getId(team?.managerId) === currentUserId).map((team) => getId(team))),
        [currentUserId, teams]
    );
    const canReviewAnyTeam = canManage || managedTeamIds.size > 0;

    const teamOptions = useMemo(() => {
        const availableTeamIds = canManage ? teams.map((team) => getId(team)) : [...managedTeamIds];

        const options = teams
            .filter((team) => availableTeamIds.includes(getId(team)))
            .map((team) => ({ label: team.name, value: getId(team) }));

        if (options.length > 1) {
            return [{ label: 'Todos los equipos', value: '' }, ...options];
        }

        return options;
    }, [canManage, managedTeamIds, teams]);

    useEffect(() => {
        const concreteOptions = teamOptions.filter((option) => option.value);
        const isValidSelection = teamOptions.some((option) => option.value === teamFilter);

        if (concreteOptions.length === 1 && !teamFilter) {
            setTeamFilter(concreteOptions[0].value);
            return;
        }

        if (teamFilter && !isValidSelection) {
            setTeamFilter(concreteOptions.length === 1 ? concreteOptions[0].value : '');
        }
    }, [teamFilter, teamOptions]);

    const ownerOptions = useMemo(() => {
        const accessibleTeamIds = canManage ? new Set(teams.map((team) => getId(team))) : managedTeamIds;
        const ownerIds = [
            ...new Set(
                keyResults
                    .filter((keyResult) => {
                        if (keyResult?.isActive === false) return false;
                        if (!accessibleTeamIds.has(getId(keyResult.teamId))) return false;
                        if (teamFilter && !belongsToOkrTeam(keyResult, teams.find(team => getId(team) === teamFilter))) return false;
                        return true;
                    })
                    .map((keyResult) => getPointOwnerId(keyResult))
                    .filter(Boolean)
            )
        ];

        const options = ownerIds.map((ownerId) => {
            const user = users.find((item) => getId(item) === ownerId);
            const keyResult = keyResults.find((item) => getPointOwnerId(item) === ownerId);
            return { label: getUserName(user || keyResult?.ownerId || ownerId), value: ownerId };
        });

        if (options.length > 1) return [{ label: 'Todos los responsables', value: '' }, ...options];
        return options;
    }, [canManage, keyResults, managedTeamIds, teamFilter, teams, users]);

    const weekOptions = useMemo(
        () =>
            weeks.map((item) => ({
                label: item.label || `Semana ${item.weekNumber}`,
                value: item.weekNumber
            })),
        [weeks]
    );

    const statusOptions = [
        { label: 'Pendiente', value: 'pending' },
        { label: 'Borrador', value: 'draft' },
        { label: 'Enviada', value: 'submitted' },
        { label: 'Aprobada', value: 'approved' },
        { label: 'Rechazada', value: 'rejected' }
    ];

    const reviewRows = useMemo(() => {
        const activeKeyResults = keyResults.filter((keyResult) => keyResult?.isActive !== false);
        return activeKeyResults
            .filter((keyResult) => {
                const team = teams.find((item) => getId(item._id) === getId(keyResult.teamId));
                if (!canManage && !managedTeamIds.has(getId(team))) return false;
                if (teamFilter && !belongsToOkrTeam(keyResult, teams.find(team => getId(team) === teamFilter))) return false;
                if (ownerFilter && getPointOwnerId(keyResult) !== ownerFilter) return false;
                return true;
            })
            .map((keyResult) => {
                const team = teams.find((item) => getId(item._id) === getId(keyResult.teamId));
                const objective = objectives.find((item) => getId(item._id) === getId(keyResult.objectiveId));
                const entry = weeklyEntries.find((item) => getId(item?.keyResultId) === getId(keyResult._id) && Number(item?.weekNumber) === Number(selectedWeekNumber));
                const status = entry?.status || 'pending';
                return {
                    ...keyResult,
                    team,
                    objective,
                    owner: users.find((item) => getId(item._id) === getPointOwnerId(keyResult)) || keyResult.ownerId || keyResult.pointOwnerUserId || keyResult.updateResponsibleUserId,
                    pointOwnerName: getPointOwnerName(keyResult, users),
                    entry,
                    status
                };
            })
            .filter((row) => !statusFilter || row.status === statusFilter);
    }, [canManage, keyResults, managedTeamIds, objectives, ownerFilter, selectedWeekNumber, statusFilter, teamFilter, teams, users, weeklyEntries]);

    const statusCounts = useMemo(() => {
        const base = { pending: 0, draft: 0, submitted: 0, approved: 0, rejected: 0 };
        const activeKeyResults = keyResults.filter((keyResult) => keyResult?.isActive !== false);

        activeKeyResults.forEach((keyResult) => {
            const team = teams.find((item) => getId(item._id) === getId(keyResult.teamId));
            if (!canManage && !managedTeamIds.has(getId(team))) return;
            if (teamFilter && !belongsToOkrTeam(keyResult, teams.find(team => getId(team) === teamFilter))) return;
            if (ownerFilter && getPointOwnerId(keyResult) !== ownerFilter) return;

            const entry = weeklyEntries.find((item) => getId(item?.keyResultId) === getId(keyResult._id) && Number(item?.weekNumber) === Number(selectedWeekNumber));
            const status = entry?.status || 'pending';
            base[status] = (base[status] || 0) + 1;
        });

        return base;
    }, [canManage, keyResults, managedTeamIds, ownerFilter, selectedWeekNumber, teamFilter, teams, weeklyEntries]);

    if (!selectedCycle?._id) {
        return (
            <div className="surface-card border-round p-4 shadow-1 text-center">
                <i className="pi pi-calendar text-4xl text-primary mb-3" />
                <h3 className="mt-0 mb-2">Selecciona o crea un ciclo OKR.</h3>
                <p className="text-color-secondary m-0">Las actualizaciones semanales aparecerán cuando exista un ciclo activo.</p>
            </div>
        );
    }

    if (!canReviewAnyTeam) {
        return (
            <div className="surface-card border-round p-4 shadow-1 text-center">
                <i className="pi pi-send text-4xl text-primary mb-3" />
                <h3 className="mt-0 mb-2">Tus actualizaciones se realizan desde Mis OKRs.</h3>
                <p className="text-color-secondary m-0">
                    Usa el tablero semanal de Mis OKRs para guardar borradores, enviar avances y corregir actualizaciones rechazadas.
                </p>
            </div>
        );
    }

    const statusBody = (rowData) => (
        <div className="flex flex-column gap-1">
            <Tag value={getStatusLabel(rowData.status)} severity={getStatusSeverity(rowData.status)} className="align-self-start" />
            {rowData.entry?.status === 'rejected' && rowData.entry?.rejectionReason ? (
                <small className="text-red-600">Motivo de rechazo: {rowData.entry.rejectionReason}</small>
            ) : null}
            {rowData.status === 'pending' ? <small className="text-color-secondary">No enviada</small> : null}
        </div>
    );

    const canReviewRow = (rowData) => {
        if (canManage) return true;
        return getId(rowData?.team?.managerId) === currentUserId;
    };

    const openRejectDialog = (rowData) => {
        setRejectEntry(rowData.entry);
        setRejectionReason('');
        setRejectionError('');
        setRejectDialogVisible(true);
    };

    const closeRejectDialog = () => {
        if (reviewingEntryId) return;
        setRejectDialogVisible(false);
        setRejectEntry(null);
        setRejectionReason('');
        setRejectionError('');
    };

    const submitReject = async () => {
        if (!rejectionReason.trim()) {
            setRejectionError('Ingresa el motivo de rechazo.');
            return;
        }

        const success = await onRejectEntry(rejectEntry, { rejectionReason: rejectionReason.trim() });
        if (success) closeRejectDialog();
    };

    const actionBody = (rowData) => {
        const reviewable = rowData.entry?.status === 'submitted' && canReviewRow(rowData);
        const isReviewing = reviewingEntryId === rowData.entry?._id;

        if (rowData.status === 'pending') {
            return <span className="text-color-secondary">No enviada</span>;
        }

        if (!reviewable) {
            return <span className="text-color-secondary">Sin acciones</span>;
        }

        return (
            <div className="flex align-items-center flex-wrap gap-2">
                <Button
                    label="Aprobar"
                    icon="pi pi-check"
                    className="p-button-sm p-button-success p-button-outlined"
                    onClick={() => onApproveEntry(rowData.entry)}
                    loading={isReviewing}
                />
                <Button
                    label="Rechazar"
                    icon="pi pi-times"
                    className="p-button-sm p-button-danger p-button-outlined"
                    onClick={() => openRejectDialog(rowData)}
                    disabled={isReviewing}
                />
            </div>
        );
    };

    const reportedValueBody = (rowData) => {
        const entry = rowData.entry;
        if (!entry) return <span className="text-color-secondary">No enviada</span>;

        if (rowData.calculationType === 'variance_forecast_vs_real') {
            const input = entry.calculationInput || {};
            return (
                <div className="flex flex-column gap-1">
                    <span>Forecast: {formatNumber(input.forecast)}</span>
                    <span>Real: {formatNumber(input.real)}</span>
                    <span>Varianza: {formatNumber(input.variance ?? entry.actualValue)}%</span>
                </div>
            );
        }

        if (rowData.calculationType === 'action_completed') {
            return Number(entry.actualValue) === 1 ? 'Completada' : 'No completada';
        }
        if (rowData.calculationType === 'max_allowed') {
            return (
                <div className="flex flex-column gap-1">
                    <span>Cantidad reportada: {formatNumber(entry.actualValue)}{rowData.unit ? ` ${rowData.unit}` : ''}</span>
                    <span>Meta máxima permitida: {formatNumber(rowData.weeklyTarget)}{rowData.unit ? ` ${rowData.unit}` : ''}</span>
                </div>
            );
        }

        return `${formatNumber(entry.actualValue)}${rowData.unit ? ` ${rowData.unit}` : ''}`;
    };

    const sourceBody = (rowData) => {
        const sourceType = rowData.entry?.sourceType || rowData.automationConfig?.sourceType || 'manual';
        if (AUTOMATED_SOURCE_LABELS[sourceType]) {
            return (
                <div className="flex flex-column gap-1">
                    <Tag value={AUTOMATED_SOURCE_LABELS[sourceType]} severity={sourceType === 'composite' ? 'warning' : 'info'} />
                    {rowData.entry?.sourceMetricKey || rowData.automationConfig?.metricKey ? (
                        <small className="text-color-secondary">{rowData.entry?.sourceMetricKey || rowData.automationConfig?.metricKey}</small>
                    ) : null}
                </div>
            );
        }
        return <span>Manual</span>;
    };

    const summaryCards = [
        { label: 'Enviadas por revisar', value: statusCounts.submitted, status: 'submitted' },
        { label: 'Pendientes de enviar', value: statusCounts.pending, status: 'pending' },
        { label: 'Aprobadas', value: statusCounts.approved, status: 'approved' },
        { label: 'Rechazadas', value: statusCounts.rejected, status: 'rejected' },
        { label: 'Borradores', value: statusCounts.draft, status: 'draft' }
    ];

    return (
        <div className="flex flex-column gap-3">
            <div className="surface-card border-round p-3 shadow-1">
                <div className="flex flex-column lg:flex-row lg:align-items-start lg:justify-content-between gap-3">
                    <div>
                        <h3 className="m-0">Revisión de actualizaciones semanales</h3>
                        <p className="text-color-secondary mt-2 mb-0">
                            Aprueba o rechaza las actualizaciones enviadas por el equipo.
                        </p>
                        <div className="flex align-items-center gap-2 mt-2">
                            <span className="text-sm text-color-secondary">Ciclo activo:</span>
                            <Tag value={selectedCycle.name} severity="info" />
                        </div>
                    </div>
                    <div className="flex flex-column sm:flex-row gap-2">
                        {currentWeek?.weekNumber ? <Tag value={`Semana actual: ${currentWeek.weekNumber}`} severity="info" className="align-self-start sm:align-self-center" /> : null}
                    </div>
                </div>
            </div>

            <div className="surface-card border-round p-3 shadow-1">
                <div className="grid align-items-end">
                    <div className="col-12 md:col-3">
                        <label className="block font-medium mb-2">Equipo</label>
                        <Dropdown
                            value={teamFilter}
                            options={teamOptions}
                            onChange={(event) => setTeamFilter(event.value || '')}
                            placeholder="Todos los equipos"
                            className="w-full"
                            disabled={!teamOptions.length}
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block font-medium mb-2">Semana</label>
                        <Dropdown
                            value={selectedWeekNumber}
                            options={weekOptions}
                            onChange={(event) => onWeekChange(event.value)}
                            placeholder="Semana actual"
                            className="w-full"
                            disabled={!weekOptions.length}
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block font-medium mb-2">Estado</label>
                        <Dropdown
                            value={statusFilter}
                            options={statusOptions}
                            onChange={(event) => setStatusFilter(event.value || 'submitted')}
                            placeholder="Enviada"
                            className="w-full"
                        />
                    </div>
                    {canReviewAnyTeam ? (
                        <div className="col-12 md:col-3">
                            <label className="block font-medium mb-2">Colaborador / Responsable</label>
                            <Dropdown
                                value={ownerFilter}
                                options={ownerOptions}
                                onChange={(event) => setOwnerFilter(event.value || '')}
                                placeholder="Todos los responsables"
                                showClear
                                filter
                                className="w-full"
                            />
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="grid">
                {summaryCards.map((card) => (
                    <div className="col-12 sm:col-6 lg:col" key={card.label}>
                        <div className="surface-card border-round p-3 shadow-1 h-full">
                            <div className="text-sm text-color-secondary mb-2">{card.label}</div>
                            <div className="flex align-items-center justify-content-between gap-2">
                                <span className="text-3xl font-semibold">{formatNumber(card.value)}</span>
                                <Tag value={getStatusLabel(card.status)} severity={getStatusSeverity(card.status)} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="surface-card border-round p-3 shadow-1">
                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2 mb-3">
                    <div>
                        <h3 className="mt-0 mb-1">Actualizaciones para revisión</h3>
                        <p className="text-color-secondary m-0">Las filas pendientes indican resultados clave que todavía no fueron enviados para la semana seleccionada.</p>
                    </div>
                </div>
                <DataTable value={reviewRows} loading={loading} responsiveLayout="scroll" emptyMessage="No hay actualizaciones para esta semana.">
                    <Column header="Semana" body={() => `Semana ${selectedWeekNumber || '-'}`} style={{ minWidth: '110px' }} />
                    <Column header="Equipo" body={(rowData) => rowData.team?.name || '-'} style={{ minWidth: '150px' }} />
                    <Column header="Responsable" body={(rowData) => rowData.pointOwnerName || getUserName(rowData.owner)} style={{ minWidth: '190px' }} />
                    <Column field="title" header="Resultado clave" sortable style={{ minWidth: '230px' }} />
                    <Column header="Tipo de captura" body={(rowData) => getCalculationTypeLabel(rowData.calculationType, rowData)} style={{ minWidth: '190px' }} />
                    <Column header="Fuente" body={sourceBody} style={{ minWidth: '140px' }} />
                    <Column header="Valor reportado" body={reportedValueBody} style={{ minWidth: '190px' }} />
                    <Column header="Puntos calculados" body={(rowData) => (rowData.entry ? formatNumber(rowData.entry.calculatedPoints) : '-')} style={{ minWidth: '140px' }} />
                    <Column header="Puntos finales" body={(rowData) => (rowData.entry ? formatNumber(rowData.entry.finalPoints) : '-')} style={{ minWidth: '130px' }} />
                    <Column header="Estado" body={statusBody} style={{ minWidth: '170px' }} />
                    <Column header="Fecha de envío" body={(rowData) => formatDate(getSubmittedDate(rowData.entry))} style={{ minWidth: '140px' }} />
                    <Column header="Acciones" body={actionBody} style={{ minWidth: '190px' }} />
                </DataTable>
            </div>

            <Dialog
                header="Rechazar actualización"
                visible={rejectDialogVisible}
                modal
                onHide={closeRejectDialog}
                draggable={false}
                style={{ width: 'min(520px, 95vw)' }}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text" onClick={closeRejectDialog} disabled={Boolean(reviewingEntryId)} />
                        <Button label="Rechazar" icon="pi pi-times" className="p-button-danger" onClick={submitReject} loading={Boolean(reviewingEntryId)} />
                    </div>
                }
            >
                <div className="flex flex-column gap-2">
                    <label className="font-medium" htmlFor="okr-rejection-reason">Motivo de rechazo</label>
                    <InputTextarea
                        id="okr-rejection-reason"
                        value={rejectionReason}
                        onChange={(event) => {
                            setRejectionReason(event.target.value);
                            setRejectionError('');
                        }}
                        rows={4}
                        className="w-full"
                        autoFocus
                    />
                    {rejectionError ? <small className="p-error">{rejectionError}</small> : null}
                </div>
            </Dialog>
        </div>
    );
};

export default OkrWeeklyUpdates;
